import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { AuthContext } from '../App';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { HardHat, SignOut, Bell, Buildings, MapPin, CurrencyDollar, CalendarBlank, ArrowsLeftRight, Star, Hammer, Trash, ClipboardText, ArrowRight, Plus } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';
import LocationSearch from '../components/LocationSearch';
import AdminNav from '../components/AdminNav';
import HelpButton from '../components/HelpButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthImage = ({ fileRef, alt, className }) => {
  const [src, setSrc] = React.useState(null);
  React.useEffect(() => {
    if (!fileRef) return;
    const fileId = fileRef.includes('/') ? fileRef.split('/').pop().split('.')[0] : fileRef;
    let url = null;
    axios.get(`${API}/files/${fileId}/download`, { responseType: 'blob' })
      .then(res => { url = window.URL.createObjectURL(res.data); setSrc(url); })
      .catch(() => {});
    return () => { if (url) window.URL.revokeObjectURL(url); };
  }, [fileRef]);
  if (!src) return <div className={className} style={{ background: 'var(--bg-elevated)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Buildings size={48} weight="duotone" style={{ color: 'var(--text-muted)' }} /></div>;
  return <img src={src} alt={alt} className={className} />;
};

const FOLDERS = [
  { id: 'main', label: 'Main Dashboard', icon: ClipboardText },
  { id: 'saved', label: 'Saved', icon: Star },
  { id: 'wip', label: 'Work in Progress', icon: Hammer },
  { id: 'trash', label: 'Trash', icon: Trash },
];

const GCDashboard = () => {
  const { user, logout, adminView, setAdminView } = useContext(AuthContext);
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [myBids, setMyBids] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFolder, setActiveFolder] = useState('main');
  const [bidFolders, setBidFolders] = useState({});
  const [projectFolders, setProjectFolders] = useState({});
  const [draggedBid, setDraggedBid] = useState(null);
  const [draggedProject, setDraggedProject] = useState(null);
  const [dragOverFolder, setDragOverFolder] = useState(null);
  const [searchResults, setSearchResults] = useState(null);
  const [searchingProjects, setSearchingProjects] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);

  useEffect(() => { fetchData(); }, []);

  // Load folder assignments from localStorage
  useEffect(() => {
    const saved = localStorage.getItem(`hb-gc-folders-${user?.id || 'default'}`);
    if (saved) setBidFolders(JSON.parse(saved));
    const savedP = localStorage.getItem(`hb-gc-proj-folders-${user?.id || 'default'}`);
    if (savedP) setProjectFolders(JSON.parse(savedP));
  }, [user]);

  // Save folder assignments
  const saveFolders = useCallback((folders) => {
    setBidFolders(folders);
    localStorage.setItem(`hb-gc-folders-${user?.id || 'default'}`, JSON.stringify(folders));
  }, [user]);

  const saveProjectFolders = useCallback((folders) => {
    setProjectFolders(folders);
    localStorage.setItem(`hb-gc-proj-folders-${user?.id || 'default'}`, JSON.stringify(folders));
  }, [user]);

  const fetchData = async () => {
    try {
      const [projectsRes, bidsRes, notificationsRes] = await Promise.all([
        axios.get(`${API}/projects`), axios.get(`${API}/bids/my-bids`), axios.get(`${API}/notifications`)
      ]);
      setProjects(projectsRes.data);
      setMyBids(bidsRes.data);
      setNotifications(notificationsRes.data);
    } catch (error) { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  };

  const handleProjectSearch = async ({ location, radius }) => {
    if (!location) { setSearchResults(null); return; }
    setSearchingProjects(true);
    try {
      const res = await axios.get(`${API}/search/projects`, { params: { location, radius } });
      setSearchResults(res.data);
      if (res.data.length === 0) toast.info('No projects found in this area');
    } catch (err) { toast.error('Search failed'); }
    finally { setSearchingProjects(false); }
  };

  const markAsRead = async (notifId) => {
    try {
      await axios.patch(`${API}/notifications/${notifId}/read`);
      setNotifications(prev => prev.map(n => n.id === notifId ? { ...n, read: true } : n));
    } catch { /* ignore */ }
  };

  const markAllRead = async () => {
    const unread = notifications.filter(n => !n.read);
    await Promise.all(unread.map(n => axios.patch(`${API}/notifications/${n.id}/read`).catch(() => {})));
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  // Drag handlers — support both bids and projects
  const handleDragStart = (e, bidId) => {
    setDraggedBid(bidId);
    setDraggedProject(null);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleProjectDragStart = (e, projectId) => {
    setDraggedProject(projectId);
    setDraggedBid(null);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, folderId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverFolder(folderId);
  };

  const handleDragLeave = () => {
    setDragOverFolder(null);
  };

  const handleDrop = (e, folderId) => {
    e.preventDefault();
    const folderLabel = FOLDERS.find(f => f.id === folderId)?.label || folderId;
    if (draggedBid) {
      const updated = { ...bidFolders };
      if (folderId === 'main') { delete updated[draggedBid]; } else { updated[draggedBid] = folderId; }
      saveFolders(updated);
      toast.success(`Bid moved to ${folderLabel}`);
    }
    if (draggedProject) {
      const updated = { ...projectFolders };
      if (folderId === 'main') { delete updated[draggedProject]; } else { updated[draggedProject] = folderId; }
      saveProjectFolders(updated);
      toast.success(`Project saved to ${folderLabel}`);
    }
    setDraggedBid(null);
    setDraggedProject(null);
    setDragOverFolder(null);
  };

  const handleDragEnd = () => {
    setDraggedBid(null);
    setDraggedProject(null);
    setDragOverFolder(null);
  };

  // Get bids for a specific folder
  const getBidsForFolder = (folderId) => {
    return myBids.filter(bid => {
      const folder = bidFolders[bid.id] || 'main';
      return folder === folderId;
    });
  };

  const getFolderCount = (folderId) => getBidsForFolder(folderId).length + getProjectsForFolder(folderId).length;

  const getProjectsForFolder = (folderId) => {
    if (folderId === 'main') return [];
    return projects.filter(p => projectFolders[p.id] === folderId);
  };

  // Time ago helper
  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const days = Math.floor(diff / 86400000);
    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days} days ago`;
    if (days < 30) return `${Math.floor(days / 7)} week${Math.floor(days / 7) > 1 ? 's' : ''} ago`;
    return `${Math.floor(days / 30)} month${Math.floor(days / 30) > 1 ? 's' : ''} ago`;
  };

  const activeBids = getBidsForFolder(activeFolder);
  const activeProjects = getProjectsForFolder(activeFolder);

  // IDs of projects the GC has already bid on
  const biddedProjectIds = new Set(myBids.map(b => b.project_id));

  // Public browse = only open/bidding projects the GC has NOT bid on
  const publicProjects = (searchResults !== null ? searchResults : projects).filter(
    p => ['open', 'bidding'].includes(p.status) && !biddedProjectIds.has(p.id)
  );

  // Private work area = projects with existing bids (awarded, in_progress, or pending bids)
  const privateProjects = projects.filter(p => biddedProjectIds.has(p.id));

  if (loading) {
    return <div className="hb-bg min-h-screen flex items-center justify-center">
      <img src="/harperbids-logo.png" alt="Loading" className="h-16 opacity-50 animate-pulse hb-logo-glow" />
    </div>;
  }

  return (
    <div className="hb-bg"><div style={{ width: '100%' }}>
      {/* Header */}
      <header className="hb-header sticky top-0 z-50" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/harperbids-logo.png" alt="HarperBids" className="h-10 hb-logo-glow" />
            <div>
              <h1 className="text-lg font-bold font-heading" style={{ color: 'var(--gold-light)' }}>HARPERBIDS</h1>
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{user.role === 'admin' ? 'Master Admin — GC View' : 'General Contractor Dashboard'}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <button
              data-testid="nav-all-jobs"
              onClick={() => navigate('/jobs')}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all"
              style={{ background: 'rgba(201,206,211,0.08)', border: '1px solid rgba(201,206,211,0.15)', color: 'var(--gold)' }}
            >
              All Jobs
            </button>
            {user.role === 'admin' && (
              <AdminNav currentView="gc" />
            )}
            <div className="font-medium text-sm" style={{ color: 'var(--gold)' }}>General Contractor</div>
            <HelpButton />
            <div className="relative">
              <button data-testid="notifications-button" onClick={() => setShowNotifications(!showNotifications)} className="relative p-2 rounded-lg btn-ghost">
                <Bell size={22} weight="duotone" style={{ color: 'var(--text-secondary)' }} />
                {notifications.filter(n => !n.read).length > 0 && (
                  <span className="absolute top-0.5 right-0.5 min-w-[18px] h-[18px] flex items-center justify-center text-[10px] font-bold rounded-full" style={{ background: '#ef4444', color: '#fff' }}>
                    {notifications.filter(n => !n.read).length}
                  </span>
                )}
              </button>
              {showNotifications && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowNotifications(false)} />
                  <div data-testid="gc-notifications-dropdown" className="absolute right-0 top-12 z-50 w-96 rounded-xl overflow-hidden" style={{ background: 'var(--bg-panel)', border: '1px solid var(--border-primary)', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}>
                    <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                      <h3 className="text-sm font-bold font-heading" style={{ color: 'var(--gold-light)' }}>Notifications</h3>
                      {notifications.filter(n => !n.read).length > 0 && (
                        <button data-testid="gc-mark-all-read" onClick={markAllRead} className="text-xs font-semibold" style={{ color: 'var(--gold)' }}>Mark all read</button>
                      )}
                    </div>
                    <div className="max-h-80 overflow-y-auto">
                      {notifications.length === 0 ? (
                        <div className="text-center py-8 text-sm" style={{ color: 'var(--text-muted)' }}>No notifications</div>
                      ) : (
                        notifications.slice(0, 20).map(n => (
                          <button key={n.id} data-testid={`gc-notif-${n.id}`} onClick={() => { markAsRead(n.id); setShowNotifications(false); }}
                            className="w-full text-left px-4 py-3 transition-colors block"
                            style={{ background: n.read ? 'transparent' : 'rgba(201,206,211,0.05)', borderBottom: '1px solid var(--border-subtle)' }}>
                            <div className="flex items-start gap-2">
                              {!n.read && <span className="w-2 h-2 rounded-full mt-1.5 shrink-0" style={{ background: 'var(--gold)' }} />}
                              <div className="min-w-0 flex-1">
                                <p className="text-xs font-semibold mb-0.5" style={{ color: n.read ? 'var(--text-muted)' : 'var(--text-primary)' }}>{n.title}</p>
                                <p className="text-xs line-clamp-2" style={{ color: 'var(--text-secondary)' }}>{n.message}</p>
                                <p className="text-[10px] mt-1" style={{ color: 'var(--text-muted)' }}>{new Date(n.created_at).toLocaleString()}</p>
                              </div>
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className="flex items-center gap-3 pl-4" style={{ borderLeft: '1px solid var(--border-primary)' }}>
              <div className="text-right">
                <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{user.name}</p>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{user.company || user.email}</p>
              </div>
              <Button data-testid="logout-button" onClick={logout} variant="ghost" size="sm" className="btn-ghost">
                <SignOut size={18} />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Body: New Bid (left) + Content (center) + Folders (right) */}
      <div className="flex" style={{ minHeight: 'calc(100vh - 73px)' }}>
        {/* Left: Browse Projects */}
        <aside className="w-20 shrink-0 flex flex-col items-center pt-6" style={{ background: 'rgba(12,12,18,0.7)', borderRight: '1px solid rgba(255,255,255,0.06)' }}>
          <button
            data-testid="new-bid-btn"
            onClick={() => setActiveFolder('browse')}
            className="flex flex-col items-center gap-2 p-3 rounded-xl cursor-pointer transition-all"
            style={{
              background: activeFolder === 'browse' ? 'rgba(201,206,211,0.15)' : 'transparent',
              color: activeFolder === 'browse' ? 'var(--gold)' : 'var(--text-secondary)',
              border: activeFolder === 'browse' ? '1px solid rgba(201,206,211,0.25)' : '1px solid transparent',
            }}
          >
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(180deg, var(--gold), var(--gold-dark))' }}>
              <Buildings size={20} weight="bold" style={{ color: '#1a1a1a' }} />
            </div>
            <span className="text-[10px] font-semibold leading-tight text-center">Browse<br/>Projects</span>
          </button>
        </aside>

        {/* Center: Main Content */}
        <main className="flex-1 p-8">
          {activeFolder === 'browse' ? (
            <>
              <h1 className="text-2xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>Available Projects</h1>
              {/* Location Search */}
              <div className="mb-6" data-testid="gc-location-search">
                <LocationSearch
                  onSearch={handleProjectSearch}
                  userLocation={user.location || ''}
                  placeholder="Search projects near a city, address, or ZIP..."
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="available-projects-grid">
                {publicProjects.map((project) => (
                  <div
                    key={project.id}
                    data-testid={`project-card-${project.id}`}
                    draggable
                    onDragStart={(e) => handleProjectDragStart(e, project.id)}
                    onDragEnd={handleDragEnd}
                    className="hb-card overflow-hidden hb-lift"
                    style={{ cursor: 'grab', opacity: draggedProject === project.id ? 0.5 : 1 }}
                  >
                    {project.photos && project.photos.length > 0 ? (
                      <div className="h-44 overflow-hidden" style={{ background: 'var(--bg-elevated)' }}>
                        <AuthImage fileRef={project.photos[0]} alt={project.title} className="w-full h-full object-cover" />
                      </div>
                    ) : (
                      <div className="h-44 flex items-center justify-center" style={{ background: 'var(--bg-elevated)' }}>
                        <Buildings size={48} weight="duotone" style={{ color: 'var(--text-muted)' }} />
                      </div>
                    )}
                    <div className="p-5">
                      <div className="flex items-start justify-between mb-1">
                        <h3 className="text-base font-semibold font-heading line-clamp-2" style={{ color: 'var(--text-primary)' }}>{project.title}</h3>
                        {project.distance_miles !== null && project.distance_miles !== undefined && (
                          <span className="text-xs px-2 py-0.5 rounded-full font-medium ml-2 shrink-0" style={{ background: 'rgba(201,206,211,0.1)', color: 'var(--gold)' }}>
                            {project.distance_miles} mi
                          </span>
                        )}
                      </div>
                      <p className="text-xs mb-3" style={{ color: 'var(--text-muted)' }}>Posted by {project.owner_name}</p>
                      <div className="space-y-1.5 mb-3">
                        <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--text-secondary)' }}><MapPin size={14} /> {project.location}</div>
                        <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--text-secondary)' }}><CurrencyDollar size={14} /> {project.budget_range}</div>
                        <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--text-secondary)' }}><CalendarBlank size={14} /> Deadline: {new Date(project.deadline).toLocaleDateString()}</div>
                      </div>
                      <div className="flex items-center justify-between">
                        {['open', 'bidding'].includes(project.status) ? (
                          <span className="flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full" style={{ background: 'rgba(201,206,211,0.1)', border: '1px solid var(--gold)', color: 'var(--gold)' }}>
                            <Plus size={12} weight="bold" /> New Bid
                          </span>
                        ) : (
                          <span className={`hb-badge hb-badge-${project.status}`}>{project.status.replace('_', ' ')}</span>
                        )}
                        <div className="flex items-center gap-2">
                          {projectFolders[project.id] && (
                            <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold" style={{ background: 'rgba(201,206,211,0.08)', color: 'var(--text-muted)', border: '1px solid var(--border-subtle)' }}>
                              {FOLDERS.find(f => f.id === projectFolders[project.id])?.label}
                            </span>
                          )}
                          <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{project.bid_count} bid{project.bid_count !== 1 ? 's' : ''}</span>
                        </div>
                      </div>
                      <button
                        data-testid={`open-project-btn-${project.id}`}
                        onClick={(e) => { e.stopPropagation(); navigate(`/projects/${project.id}`); }}
                        className="w-full mt-3 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-xs font-semibold transition-all"
                        style={{ background: 'rgba(201,206,211,0.08)', border: '1px solid rgba(201,206,211,0.2)', color: 'var(--gold)', cursor: 'pointer' }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(201,206,211,0.15)'; e.currentTarget.style.borderColor = 'var(--gold)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(201,206,211,0.08)'; e.currentTarget.style.borderColor = 'rgba(201,206,211,0.2)'; }}
                      >
                        View &amp; Bid <ArrowRight size={14} weight="bold" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              {searchingProjects && (
                <div className="text-center py-8" style={{ color: 'var(--text-muted)' }}>Searching...</div>
              )}
              {publicProjects.length === 0 && !searchingProjects && (
                <div className="hb-empty p-12 text-center">
                  <Buildings size={64} weight="duotone" className="mx-auto mb-4" style={{ color: 'var(--text-muted)' }} />
                  <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
                    {searchResults !== null ? 'No projects found in this area' : 'No new projects available'}
                  </h3>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                    {searchResults !== null ? 'Try a wider radius or different location' : 'You\'ve bid on all available projects. Check back later!'}
                  </p>
                </div>
              )}
            </>
          ) : (
            <>
              <h1 className="text-2xl font-semibold mb-6" style={{ color: 'var(--text-primary)' }}>
                {FOLDERS.find(f => f.id === activeFolder)?.label}
              </h1>

              {/* My Active Projects — projects I've bid on (only on Main Dashboard) */}
              {activeFolder === 'main' && privateProjects.length > 0 && (
                <div className="mb-8">
                  <h3 className="text-xs font-semibold uppercase tracking-wide mb-3" style={{ color: 'var(--text-muted)' }}>My Active Projects ({privateProjects.length})</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {privateProjects.map(project => {
                      const bid = myBids.find(b => b.project_id === project.id);
                      return (
                        <div key={project.id} data-testid={`active-project-card-${project.id}`}
                          draggable
                          onDragStart={(e) => bid ? handleDragStart(e, bid.id) : handleProjectDragStart(e, project.id)}
                          onDragEnd={handleDragEnd}
                          className="hb-card p-5 hb-lift"
                          style={{ cursor: 'grab', opacity: (bid && draggedBid === bid.id) || draggedProject === project.id ? 0.5 : 1, border: project.status === 'awarded' ? '1px solid rgba(34,197,94,0.3)' : '1px solid var(--border-primary)' }}>
                          <div className="flex items-start justify-between mb-2 gap-2">
                            <h4 className="text-sm font-semibold font-heading line-clamp-2" style={{ color: 'var(--text-primary)' }}>{project.title}</h4>
                            <span className={`hb-badge hb-badge-${project.status} shrink-0`}>{project.status.replace('_', ' ')}</span>
                          </div>
                          <div className="space-y-1 mb-2">
                            <div className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--text-muted)' }}><MapPin size={12} /> {project.location}</div>
                            <div className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--text-muted)' }}><CurrencyDollar size={12} /> {project.budget_range}</div>
                          </div>
                          {bid && (
                            <div className="flex items-center justify-between mb-3 px-2.5 py-1.5 rounded-lg" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
                              <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Your bid</span>
                              <span className="text-sm font-bold" style={{ color: 'var(--gold)' }}>${bid.amount?.toLocaleString()}</span>
                              <span className={`hb-badge hb-badge-${bid.status}`} style={{ fontSize: '9px', padding: '1px 6px' }}>{bid.status.replace('_', ' ')}</span>
                            </div>
                          )}
                          <button data-testid={`active-project-open-${project.id}`} onClick={() => navigate(`/projects/${project.id}`)}
                            className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold transition-all"
                            style={{ background: 'rgba(201,206,211,0.08)', border: '1px solid rgba(201,206,211,0.2)', color: 'var(--gold)', cursor: 'pointer' }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(201,206,211,0.15)'; e.currentTarget.style.borderColor = 'var(--gold)'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(201,206,211,0.08)'; e.currentTarget.style.borderColor = 'rgba(201,206,211,0.2)'; }}>
                            {project.status === 'awarded' ? 'Open Project Hub' : 'View Project'} <ArrowRight size={12} weight="bold" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Saved Projects in this folder (non-main folders) */}
              {activeProjects.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-xs font-semibold uppercase tracking-wide mb-3" style={{ color: 'var(--text-muted)' }}>Saved Projects ({activeProjects.length})</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {activeProjects.map(project => (
                      <div key={project.id} draggable onDragStart={(e) => handleProjectDragStart(e, project.id)} onDragEnd={handleDragEnd}
                        className="hb-card p-5 hb-lift" style={{ cursor: 'grab', opacity: draggedProject === project.id ? 0.5 : 1 }}>
                        <div className="flex items-start justify-between mb-2 gap-2">
                          <h4 className="text-sm font-semibold font-heading line-clamp-2" style={{ color: 'var(--text-primary)' }}>{project.title}</h4>
                          <span className="flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full shrink-0" style={{ background: 'rgba(201,206,211,0.1)', border: '1px solid var(--gold)', color: 'var(--gold)' }}><Plus size={10} weight="bold" /> New Bid</span>
                        </div>
                        <div className="space-y-1 mb-3">
                          <div className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--text-muted)' }}><MapPin size={12} /> {project.location}</div>
                          <div className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--text-muted)' }}><CurrencyDollar size={12} /> {project.budget_range}</div>
                        </div>
                        <button data-testid={`folder-project-open-${project.id}`} onClick={(e) => { e.stopPropagation(); navigate(`/projects/${project.id}`); }}
                          className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold transition-all"
                          style={{ background: 'rgba(201,206,211,0.08)', border: '1px solid rgba(201,206,211,0.2)', color: 'var(--gold)', cursor: 'pointer' }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(201,206,211,0.15)'; e.currentTarget.style.borderColor = 'var(--gold)'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(201,206,211,0.08)'; e.currentTarget.style.borderColor = 'rgba(201,206,211,0.2)'; }}>
                          View &amp; Bid <ArrowRight size={12} weight="bold" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Bids in this folder */}
              {activeBids.length > 0 && activeProjects.length > 0 && (
                <h3 className="text-xs font-semibold uppercase tracking-wide mb-3" style={{ color: 'var(--text-muted)' }}>Submitted Bids ({activeBids.length})</h3>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="bids-container">
                {activeBids.map((bid) => (
                  <div key={bid.id} data-testid={`bid-card-${bid.id}`} draggable onDragStart={(e) => handleDragStart(e, bid.id)} onDragEnd={handleDragEnd}
                    className="hb-card p-6 hb-lift" style={{ opacity: draggedBid === bid.id ? 0.5 : 1, borderColor: 'rgba(255,255,255,0.08)', borderRadius: '20px', cursor: 'grab' }}>
                    <div className="text-base font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>{bid.project_title || `Bid #${bid.id.slice(0, 8)}`}</div>
                    <div className="text-sm mb-2" style={{ color: 'var(--gold)' }}>${bid.amount.toLocaleString()} &bull; {timeAgo(bid.submitted_at)}</div>
                    {bid.status === 'changes_requested' && bid.change_request_message && (
                      <div className="text-xs px-2 py-1.5 rounded mb-2" style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)', color: '#fbbf24' }}>Changes requested: {bid.change_request_message}</div>
                    )}
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-xs" style={{ color: 'var(--text-muted)' }}>{user.company || user.name} &bull; {bid.estimated_duration}</div>
                      <span className={`hb-badge hb-badge-${bid.status}`}>{bid.status.replace('_', ' ')}</span>
                    </div>
                    {bid.status === 'changes_requested' ? (
                      <button data-testid={`edit-bid-${bid.id}`} onClick={(e) => { e.stopPropagation(); navigate(`/projects/${bid.project_id}`); }}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-xs font-semibold transition-all"
                        style={{ background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.35)', color: '#fbbf24', cursor: 'pointer' }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(245,158,11,0.2)'; e.currentTarget.style.borderColor = '#f59e0b'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(245,158,11,0.12)'; e.currentTarget.style.borderColor = 'rgba(245,158,11,0.35)'; }}>
                        Edit & Resubmit Bid <ArrowRight size={14} weight="bold" />
                      </button>
                    ) : (
                    <button data-testid={`view-project-${bid.id}`} onClick={(e) => { e.stopPropagation(); navigate(`/projects/${bid.project_id}`); }}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-xs font-semibold transition-all"
                      style={{ background: 'rgba(201,206,211,0.08)', border: '1px solid rgba(201,206,211,0.2)', color: 'var(--gold)', cursor: 'pointer' }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(201,206,211,0.15)'; e.currentTarget.style.borderColor = 'var(--gold)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(201,206,211,0.08)'; e.currentTarget.style.borderColor = 'rgba(201,206,211,0.2)'; }}>
                      View Project <ArrowRight size={14} weight="bold" />
                    </button>
                    )}
                  </div>
                ))}
              </div>
              {activeBids.length === 0 && activeProjects.length === 0 && (
                <div className="hb-empty p-12 text-center">
                  <HardHat size={64} weight="duotone" className="mx-auto mb-4" style={{ color: 'var(--text-muted)' }} />
                  <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
                    {activeFolder === 'main' ? 'No bids yet' : `No items in ${FOLDERS.find(f => f.id === activeFolder)?.label}`}
                  </h3>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                    {activeFolder === 'main' ? 'Browse projects and submit your first bid' : 'Drag projects or bids here to organize them'}
                  </p>
                </div>
              )}
            </>
          )}
        </main>

        {/* Right: Folders Panel */}
        <aside className="w-72 shrink-0 p-6" style={{ background: 'rgba(12,12,18,0.7)', borderLeft: '1px solid rgba(255,255,255,0.06)' }}>
          <h3 className="text-xs font-medium uppercase tracking-widest mb-4" style={{ color: 'var(--text-muted)' }}>Folders</h3>
          {FOLDERS.map(folder => {
            const Icon = folder.icon;
            const isActive = activeFolder === folder.id;
            const isDragOver = dragOverFolder === folder.id;
            return (
              <div
                key={folder.id}
                data-testid={`folder-${folder.id}`}
                onClick={() => setActiveFolder(folder.id)}
                onDragOver={(e) => handleDragOver(e, folder.id)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, folder.id)}
                className="flex items-center gap-3 px-4 py-4 rounded-2xl mb-2 cursor-pointer transition-all"
                style={{
                  background: isDragOver ? 'rgba(201,206,211,0.15)' : isActive ? 'rgba(201,206,211,0.1)' : 'transparent',
                  color: isActive ? 'var(--gold)' : 'var(--text-primary)',
                  border: isDragOver ? '1px dashed var(--gold)' : '1px solid transparent',
                }}
              >
                <Icon size={24} weight={isActive ? 'fill' : 'duotone'} />
                <div className="flex-1">
                  <div className="font-semibold text-sm">{folder.label}</div>
                  <div className="text-xs" style={{ color: 'var(--text-muted)' }}>{getFolderCount(folder.id)} item{getFolderCount(folder.id) !== 1 ? 's' : ''}</div>
                </div>
              </div>
            );
          })}

          {/* Browse Projects link */}
          <div className="mt-6 pt-4" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            <button
              data-testid="browse-projects-btn"
              onClick={() => setActiveFolder('browse')}
              className="flex items-center gap-3 px-4 py-4 rounded-2xl w-full cursor-pointer transition-all"
              style={{
                background: activeFolder === 'browse' ? 'rgba(143,149,155,0.08)' : 'transparent',
                color: activeFolder === 'browse' ? 'var(--cyan)' : 'var(--text-secondary)',
              }}
            >
              <Buildings size={24} weight="duotone" />
              <div className="flex-1 text-left">
                <div className="font-semibold text-sm">Browse Projects</div>
                <div className="text-xs" style={{ color: 'var(--text-muted)' }}>{publicProjects.length} available</div>
              </div>
            </button>
          </div>
        </aside>
      </div>
    </div></div>
  );
};

export default GCDashboard;
